import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import { RichText } from "prismic-reactjs";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import StrainByID from "../components/StrainByID";
import { Carousel } from "react-responsive-carousel";
import unknownStrain from "../images/unknown-strain.jpg";
import gg4 from "../images/gg4.png";
import { Button } from "@material-ui/core";
import renderButtons from "../helpers/renderButtons";
import Select from "@material-ui/core/Select";

export default function TerpeneFinderSlice(slice) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("768"));

  const [terpenes, setTerpenes] = useState(false);
  const [terpeneCategories, setTerpeneCategories] = useState(false);

  const [selectedTaste, setSelectedTaste] = useState("I taste");
  const [selectedSmell, setSelectedSmell] = useState("I smell");

  const [terpeneMatch, setTerpeneMatch] = useState(false);

  const [disabled, setDisabled] = useState({ tastes: [], smells: [] });

  const loading = useSelector((state) =>
    get(state, "prismic.terpenes_list.loading", true)
  );
  const data = useSelector((state) =>
    get(state, "prismic.terpenes_list.data", false)
  );

  useEffect(() => {
    dispatch({
      type: "PRISMIC_LOAD_REPEATABLE",
      key: "terpene",
      reduxKey: "terpenes_list",
    });
  }, [dispatch]);

  useEffect(() => {
    if (!loading && data) {
      //remove cymene for now
      const filteredData = data.filter((el) => {
        return get(el, "data.terpene_name[0].text") !== "Cymene";
      });
      setTerpenes(filteredData);
    }
  }, [data, loading]);

  useEffect(() => {
    if (terpenes) {
      const smells = ["I smell"];
      const tastes = ["I taste"];
      terpenes.map((terpene) => {
        smells.push(get(terpene, "data.smell"));
        tastes.push(get(terpene, "data.taste"));
        return null;
      });

      const uniqueSmells = [...new Set(smells)];
      const uniqueTastes = [...new Set(tastes)];

      setTerpeneCategories({ uniqueSmells, uniqueTastes });
    }
  }, [terpenes]);

  useEffect(() => {
    if (terpenes) {
      const terpeneMatch = terpenes.filter(
        (el) =>
          el.data.smell === selectedSmell && el.data.taste === selectedTaste
      );
      if (terpeneMatch.length > 0) {
        setTerpeneMatch(terpeneMatch[0]);
      } else {
        setTerpeneMatch(false);
      }
    }
  }, [selectedSmell, selectedTaste, terpenes]);

  useEffect(() => {
    if (terpenes && selectedSmell !== "I smell") {
      const disabledTastes = [];
      const enabledTastes = [];
      terpenes.map((el) => {
        if (el.data.smell !== selectedSmell) {
          disabledTastes.push(get(el, "data.taste"));
        } else {
          enabledTastes.push(get(el, "data.taste"));
        }
        return null;
      });
      const finalExcludedTastes = disabledTastes.filter(
        (n) => !enabledTastes.includes(n)
      );
      setDisabled({ ...disabled, tastes: finalExcludedTastes });
    }
    if (terpenes && selectedSmell === "I smell") {
      setDisabled({ ...disabled, tastes: [] });
    }
  }, [selectedSmell, terpenes]);

  useEffect(() => {
    if (terpenes && selectedTaste !== "I taste") {
      const disabledSmells = [];
      const enabledSmells = [];
      terpenes.map((el) => {
        if (el.data.taste !== selectedTaste) {
          disabledSmells.push(get(el, "data.smell"));
        } else {
          enabledSmells.push(get(el, "data.smell"));
        }
        return null;
      });

      const finalExcludedSmells = disabledSmells.filter(
        (n) => !enabledSmells.includes(n)
      );
      setDisabled({ ...disabled, smells: finalExcludedSmells });
    }

    if (terpenes && selectedTaste === "I taste") {
      setDisabled({ ...disabled, smells: [] });
    }
  }, [selectedTaste, terpenes]);

  const handleSmellChange = (event) => {
    setSelectedSmell(event.target.value);
  };
  const handleTasteChange = (event) => {
    setSelectedTaste(event.target.value);
  };

  const reset = () => {
    setSelectedTaste("I taste");
    setSelectedSmell("I smell");
    setDisabled({ tastes: [], smells: [] });
  };

  const buttons = [
    {
      active: true,
      cta_link: {
        uid: "shop/",
        target: "_blank",
      },
      cta_text: "Shop",
      button_variant: "contained",
      button_color: "secondary",
    },
  ];

  const terpeneButtonStyle = {
    backgroundColor: get(terpeneMatch, "data.color"),
  };

  return (
    <div className="terpene-finder-slice cool-margin">
      <div className="content">
        <div className="content-wrapper">
          <RichText render={slice.primary.content} />
        </div>
      </div>
      <div className="wrapper">
        {terpeneCategories && (
          <div className="filters">
            <FormControl className="form-control">
              <Select
                id="select-smells"
                name="select-smells"
                value={selectedSmell}
                onChange={handleSmellChange}
                variant="outlined"
                color="primary"
                placeholder="Select a Smell"
                displayEmpty
              >
                {terpeneCategories.uniqueSmells.map((smell, i) => (
                  <MenuItem
                    disabled={disabled.smells.includes(smell)}
                    key={i}
                    value={smell}
                  >
                    {smell}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className="form-control">
              <Select
                id="select-taste"
                name="select-taste"
                value={selectedTaste}
                displayEmpty
                onChange={handleTasteChange}
                variant="outlined"
                color="primary"
              >
                {terpeneCategories.uniqueTastes.map((taste, i) => (
                  <MenuItem
                    disabled={disabled.tastes.includes(taste)}
                    key={i}
                    value={taste}
                  >
                    {taste}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(selectedTaste !== "I taste" || selectedSmell !== "I smell") && (
              <Button
                onClick={reset}
                color="primary"
                variant="contained"
                disableElevation
                style={{ backgroundColor: "transparent" }}
              >
                Reset
              </Button>
            )}
          </div>
        )}

        <div className="selected-terpene">
          <h3 className="terpene-icon" style={terpeneButtonStyle}>
            {get(terpeneMatch, "data.terpene_name[0].text") || "?"}
          </h3>
        </div>

        <h6>Recommended Strain</h6>
        <div className="recommended-strains">
          {terpeneMatch &&
          get(terpeneMatch, "data.terpene_name[0].text") !== "Nerolidol" ? (
            isMobile ? (
              <Carousel
                className="strains-carousel"
                showThumbs={false}
                showStatus={false}
                showIndicators={false}
              >
                {terpeneMatch &&
                  terpeneMatch.data.strains.map((strain, i) => (
                    <StrainByID key={i} id={strain.strain.uid} />
                  ))}
              </Carousel>
            ) : (
              <div className="strain-cards-desktop">
                {terpeneMatch &&
                  terpeneMatch.data.strains.map((strain, i) => (
                    <StrainByID key={i} id={strain.strain.uid} />
                  ))}
              </div>
            )
          ) : get(terpeneMatch, "data.terpene_name[0].text") !== "Nerolidol" ? (
            <div className="strains-carousel">
              <img src={unknownStrain} alt="Unknown Strain" />
            </div>
          ) : (
            <div className="strains-carousel">
              <img src={gg4} alt="GG4" />
            </div>
          )}

          <div className="button-container">{renderButtons(buttons)}</div>
        </div>
      </div>
    </div>
  );
}
